import Box from '@mui/material/Box';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MFTextField, MFSubmitButton } from '../../lib/formik';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { emailRegex, passwordRegex, phoneRegExp } from '../../utils/regex';
import { CountryCodesDropDown, LoginLayout } from '../commonComponents';
import { getCountryCodes, preventSpecialCharacters } from '../../utils/utilityFunctions';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { FormikPasswordField } from '../../lib/formik/TextField';
import { NavLink } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import { sendLoginOTP } from '../../redux-store/actions/userManagement';
import { SendLoginOTPResponse } from '../../redux-store/types/user';
import { ENABLE_EMAIL } from '../../utils/constant';

interface Values {
  phone?: string;
  countryCode: string;
  countryNameAndCode: string;
  password: string;
  email?: string;
}

const validationSchema = yup.object().shape({
  phone: yup.string().test('phone', (value, context) => {
    const { createError, path, parent } = context;
    const codesList = getCountryCodes()
      .map((codes) => {
        if (codes.label === parent.countryNameAndCode) {
          return codes.countryCode;
        }
      })
      .filter((ele) => ele)
      .toString();
    if (!ENABLE_EMAIL) {
      if (!value) {
        return createError({
          message: 'Mobile number is required',
          path,
        });
      }
      if (parent.countryNameAndCode === 'India: +91' && !phoneRegExp.test(value)) {
        return createError({
          message: 'Invalid Mobile number',
          path,
        });
      }
      if (!isValidPhoneNumber(value, codesList as CountryCode)) {
        return createError({
          message: 'Invalid Mobile number',
          path,
        });
      }
    }
    return true;
  }),
  countryNameAndCode: yup
    .string()
    .nullable()
    .test('countryNameAndCode', (value, context) => {
      const { createError, path, parent } = context;
      const codesList = getCountryCodes()
        .map((codes) => {
          if (codes.label === value) {
            return codes.countryCode;
          }
        })
        .filter((ele) => ele)
        .toString();
      if (!ENABLE_EMAIL) {
        if (!value) {
          return createError({
            message: 'Country Code is required',
            path,
          });
        }
        if (parent.phone && !isValidPhoneNumber(parent.phone, codesList as CountryCode)) {
          return createError({
            message: 'Invalid Mobile number',
            path,
          });
        }
      }

      return true;
    }),
  password: yup.string().nullable().required('Password is required'),
  // .matches(
  //   passwordRegex,
  //   'Password must be at least 8 characters long and must contain at least one uppercase letter, one lowercase letter, one numeral, and one special character'
  // ),
  email: yup
    .string()
    .nullable()
    .when({
      is: () => ENABLE_EMAIL,
      then: yup
        .string()
        .nullable()
        .required('Email is Required')
        .matches(emailRegex, 'Invalid Email Id'),
    }),
});

export default function PasswordBasedLogin(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const onSubmit = async (values: Values) => {
    try {
      const { phone = '', countryNameAndCode = 'India: +91', password = '', email = '' } = values;
      const res = (await dispatch(
        sendLoginOTP({
          ...(ENABLE_EMAIL
            ? { email: email }
            : { phone: phone.trim(), countryCode: countryNameAndCode?.split(':')[1].trim() }),
          // countryCode: countryNameAndCode?.split(':')[1].trim(),
          password,
        })
      )) as unknown as SendLoginOTPResponse;
      history.push('verify-otp', {
        countryCode: countryNameAndCode?.split(':')[1].trim(),
        ...(res || {}),
        mobile: phone.trim(),
        email: email,
        password,
        loginType: 'password-based-login',
      });
    } catch (e) {
      console.error((e as unknown as Error).message);
    }
  };
  const initialValues: Values = {
    phone: '',
    countryCode: '+91',
    countryNameAndCode: 'India: +91',
    password: '',
    email: '',
  };
  const [mobileElementWidth, setMobileElementWidth] = useState(null);
  useEffect(() => {
    const mobileElement = document.getElementsByName('phone');
    setMobileElementWidth(mobileElement[0]?.parentElement?.clientWidth as any);
  }, []);
  return (
    <LoginLayout LoginText="Login">
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
        {({ handleSubmit, values }) => (
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              '.MuiLoadingButton-root': {
                mt: 5,
              },
              '.MuiInputLabel-root': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: 16,
                fontWeight: 500,
              },
            }}>
            {ENABLE_EMAIL ? (
              <MFTextField
                autoComplete="off"
                label="Email ID"
                name="email"
                placeholder="Enter Email ID"
                required
              />
            ) : (
              <MFTextField
                label="Mobile Number"
                name="phone"
                placeholder="Enter Mobile number"
                onKeyDown={(e) => {
                  preventSpecialCharacters(e);
                }}
                startAdornment={
                  <CountryCodesDropDown
                    ElementWidth={mobileElementWidth && mobileElementWidth}
                    name={'countryNameAndCode'}
                    value={values.countryNameAndCode}
                  />
                }
                autoFocus
              />
            )}
            <FormikPasswordField label="Password" name="password" placeholder="Enter Password" />
            <Stack justifyContent={'center'}>
              <Typography
                component={NavLink}
                to={{ pathname: '/password-reset' }}
                sx={{
                  textAlign: 'center',
                  textDecoration: 'none',
                  color: (theme) => theme.palette.primary.main,
                  fontWeight: 500,
                  letterSpacing: 1,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}>
                Forgot password?
              </Typography>
            </Stack>
            <MFSubmitButton label="Get OTP" />
          </Box>
        )}
      </Formik>
    </LoginLayout>
  );
}
